import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Eye, Pencil, Share2 } from "lucide-react"
import ItemCover from "components/ItemCover"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { TableCell, TableRow } from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useAlert } from "providers/AlertProvider"
import { type Product } from "types"
import { formatProductTypeName } from "utils/formatProductTypeName"
import { ProductStatusBadge } from "./ProductStatusBadge"

export interface MakeupRowProps {
  product: Product
  selected: boolean
  onToggleSelect: (id: string) => void
  onViewDemo: (product: Product) => void
  onGenerateQrCode: (product: Product) => void
  onEdit: (productId: string) => void
  toggleVisibility: (product: Product) => void
}

const actionButtonClassName = "h-8 gap-1 px-1.5 text-foreground/70 hover:text-foreground"

export const MakeupRow: React.FC<MakeupRowProps> = ({
  product,
  selected,
  onToggleSelect,
  onViewDemo,
  onGenerateQrCode,
  onEdit,
  toggleVisibility,
}) => {
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const [visibilityLoading, setVisibilityLoading] = useState(false)

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement
    if (
      target.closest("button") ||
      target.closest('[role="switch"]') ||
      target.closest('[role="checkbox"]') ||
      target.closest("label")
    )
      return
    onToggleSelect(product.id)
  }

  const handleToggleVisibility = async () => {
    setVisibilityLoading(true)
    try {
      await toggleVisibility(product)
    } catch {
      showAlert({ error: true, text: t("productTableRow.alertVisibilityUpdateFailed") })
    } finally {
      setVisibilityLoading(false)
    }
  }

  const isVisible = Boolean(product.visible)
  const cover = product.cover && product.cover.length ? product.cover : "/none-image.svg"
  const hasConfiguredVariant =
    product.colors?.some((color) => color.trim().length > 0) ||
    product.skus?.some((sku) => sku.vto?.color?.trim().length)
  const isSetupComplete = Boolean(product.type?.trim() && hasConfiguredVariant)

  return (
    <TableRow
      data-testid="makeup-product-row"
      data-product-id={product.id}
      data-state={selected ? "selected" : undefined}
      className="group cursor-pointer data-[state=selected]:bg-vto-accent-soft"
      onClick={handleRowClick}
    >
      <TableCell className="pl-3">
        <Checkbox
          data-testid="makeup-product-select"
          aria-label={`Select ${product.name}`}
          checked={selected}
          onCheckedChange={() => onToggleSelect(product.id)}
          className={
            selected
              ? ""
              : "opacity-0 transition-opacity group-hover:opacity-60 group-hover:hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          }
        />
      </TableCell>
      <TableCell className="min-w-0">
        <div className="flex min-w-0 items-center gap-3">
          <div className="shrink-0 overflow-hidden rounded-md bg-muted/40">
            <ItemCover cover={cover} name={product.name} size={40} />
          </div>
          <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
            <span className="min-w-0 truncate text-sm font-medium">{product.name}</span>
            {product.brand && (
              <span className="min-w-0 truncate text-sm text-muted-foreground">
                {product.brand}
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="min-w-0">
        <span className="block truncate text-sm text-foreground">
          {product.type ? formatProductTypeName(product.type) : ""}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <ProductStatusBadge variant={isSetupComplete ? "ready" : "needSetup"}>
          {t(isSetupComplete ? "makeup.status.ready" : "makeup.status.needSetup")}
        </ProductStatusBadge>
      </TableCell>
      <TableCell className="whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Switch
            data-testid="makeup-product-visibility"
            className="cursor-pointer"
            disabled={visibilityLoading}
            checked={isVisible}
            onCheckedChange={() => void handleToggleVisibility()}
          />
          <span className="text-sm text-foreground/70">
            {t(isVisible ? "makeup.tryOnButton.on" : "makeup.tryOnButton.off")}
          </span>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
        <div className="flex justify-end gap-1">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    data-testid="makeup-product-view-demo"
                    variant="ghost"
                    size="sm"
                    className={actionButtonClassName}
                    disabled={!isVisible}
                    aria-disabled={!isVisible}
                    onClick={() => onViewDemo(product)}
                  >
                    <Eye className="size-4" />
                    {t("makeup.action.viewDemo")}
                  </Button>
                </span>
              </TooltipTrigger>
              {!isVisible ? (
                <TooltipContent>{t("makeup.action.viewDemoDisabledHint")}</TooltipContent>
              ) : null}
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="makeup-product-share"
                  aria-label={t("makeup.qrCode.actionLabel")}
                  variant="ghost"
                  size="sm"
                  className={actionButtonClassName}
                  onClick={() => onGenerateQrCode(product)}
                >
                  <Share2 className="size-4" />
                  {t("makeup.action.share")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("makeup.qrCode.tooltip")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            data-testid="makeup-product-edit"
            variant="ghost"
            size="sm"
            className={actionButtonClassName}
            onClick={() => onEdit(product.id)}
          >
            <Pencil className="size-4" />
            {t("makeup.action.edit")}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}
